import { Fragment } from 'react';
import { Field } from '@herit-fe/heritage-ui';
import { daysBetween, getKstToday } from '@/shared/lib';
import { UI_TEXT } from '@/shared/languages';
import { DateField } from './date-field';
import { FieldLabel } from './field-label';
import { INLINE_FIELD_SURFACE, type FilterFieldControl } from './filter-field';
import { MonthField } from './month-field';
import type { PeriodType } from './period-math';
import { SelectField, type SelectOption, YEAR_OPTIONS } from './select-field';

/**
 * 날짜기준·값의 **팝오버 동반 그룹** — 둘은 서로를 바꾸는 짝이라 칩 팝오버에 함께 뜬다(칩은 그대로 둘).
 * 기준을 바꾸면 값이 그 단위 기본으로 cascade 리셋되므로, 한쪽만 띄우면 곧바로 다른 칩을 열게 된다.
 */
const PERIOD_GROUP = 'period';

const UNIT_LABEL: Record<PeriodType, string> = {
  day: UI_TEXT.TERM_DAY,
  month: UI_TEXT.TERM_MONTH,
  year: UI_TEXT.TERM_YEAR,
  range: UI_TEXT.TERM_PERIOD,
};

export interface PeriodFilterControlsProps {
  period: PeriodType;
  date: string;
  startDate: string;
  endDate: string;
  setPeriod: (period: PeriodType) => void;
  setDate: (date: string) => void;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  /** 노출 날짜기준 — 발전량=[day,month,year,range], 수익=[month,year,range]. */
  periods: PeriodType[];
  /** Field id prefix(a11y) — 호출처 useId. */
  idPrefix: string;
  /**
   * range 조회 상한(일) — **주면** 초과 시 종료일 아래에 인라인 경고를 낸다(입력은 그대로 허용).
   * 미주입이면 경고 없음 — 상한을 강제하지 않는 화면(수익 등)은 종전대로 동작한다.
   */
  maxRangeDays?: number;
}

/**
 * 조회조건 기간 컨트롤(발전량·수익 공용)을 **차원별로 분해**한 목록 — 같은 JSX 한 벌이 조회조건 드로워
 * (`FilterFieldStack`)와 칩 팝오버 양쪽에 중복 없이 쓰인다.
 *
 * 칩은 이보다 **성기다** — `periodChipSpecs` 는 `period` 칩 하나만 내고, `dateType` 은 자기 칩 없이 같은
 * `PERIOD_GROUP` 으로 그 칩에 함께 실린다(`attachFieldControls`). 값 포맷이 이미 단위를 드러내(yyyy-MM-dd /
 * yyyy-MM / yyyy / 시작~종료) 기준을 따로 칩으로 세울 정보량이 없고, 편집은 어차피 둘이 한 팝오버라서다.
 *
 * `period` 차원은 날짜기준에 따라 **필드 개수가 갈린다**(단일 기준값 1개 / 기간 = 시작일+종료일 2개) —
 * 그래서 컨트롤 단위는 '입력 하나'가 아니라 **논리 차원 하나**다.
 */
export function periodFilterFields({
  period,
  date,
  startDate,
  endDate,
  setPeriod,
  setDate,
  setStartDate,
  setEndDate,
  periods,
  idPrefix,
  maxRangeDays,
}: PeriodFilterControlsProps): FilterFieldControl[] {
  const today = getKstToday();
  // 초과 판정은 **표시용**(조회 차단은 호출처가 `isPeriodQueryable` 로 한다) — 여기선 왜 결과가 없는지를
  // 값 바로 아래에서 말해 주는 역할만 한다.
  const rangeExceeded =
    maxRangeDays != null && period === 'range' && daysBetween(startDate, endDate) > maxRangeDays;
  const fid = (key: string) => `${idPrefix}-${key}`;
  const dateTypeOptions: SelectOption[] = periods.map((p) => ({ value: p, label: UNIT_LABEL[p] }));

  return [
    {
      key: 'dateType',
      label: UI_TEXT.PERIOD_FILTER_DATE_TYPE,
      // 값과 **한 팝오버**(group) — 기준을 바꾸면 값이 cascade 리셋되므로 바로 이어서 값을 골라야 한다.
      // 그래서 기준 변경은 **닫지 않는다**(close 미호출) — 닫으면 값 고치러 다른 칩을 또 열게 된다.
      group: PERIOD_GROUP,
      render: () => (
        <Field>
          <FieldLabel htmlFor={fid('dateType')}>{UI_TEXT.PERIOD_FILTER_DATE_TYPE}</FieldLabel>
          <SelectField
            id={fid('dateType')}
            label={UI_TEXT.PERIOD_FILTER_DATE_TYPE}
            options={dateTypeOptions}
            value={period}
            onChange={(v) => setPeriod(v as PeriodType)}
          />
        </Field>
      ),
    },
    {
      key: 'period',
      label: UI_TEXT.PERIOD_FILTER_DATE_DIM(period),
      group: PERIOD_GROUP,
      // 값 필드 — 일/월/연=단일 기준값(고르면 끝 → `close`), 기간=시작일+종료일(두 필드라 닫지 않는다).
      render: ({ close }) =>
        period === 'range' ? (
          <>
            <Field>
              <FieldLabel htmlFor={fid('startDate')}>{UI_TEXT.TERM_START_DATE}</FieldLabel>
              <DateField
                id={fid('startDate')}
                label={UI_TEXT.TERM_START_DATE}
                value={startDate}
                // **캘린더의 유일한 제약은 오늘까지**(미래는 데이터가 없다). 시작·종료를 서로 묶어 두지 않는다 —
                // 역전은 막는 게 아니라 **고른 쪽을 존중하고 반대쪽을 같은 값으로** 맞춘다(`clampDateRange`).
                // 교차 제약(시작 ≤ 종료)을 입력에 걸면 "종료를 앞으로 당기려면 시작부터 고쳐야" 하는 순서 강요가 된다.
                max={today}
                onChange={setStartDate}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={fid('endDate')}>{UI_TEXT.TERM_END_DATE}</FieldLabel>
              <DateField
                id={fid('endDate')}
                label={UI_TEXT.TERM_END_DATE}
                value={endDate}
                // 시작일과 같은 규칙 — 하한 없음, 상한은 오늘. 역전은 `clampPeriodRange` 가 같은 값으로 흡수한다.
                max={today}
                onChange={setEndDate}
              />
            </Field>
            {rangeExceeded ? (
              <p role="alert" className="text-xs text-destructive">
                {UI_TEXT.PERIOD_FILTER_MAX_RANGE(maxRangeDays)}
              </p>
            ) : null}
          </>
        ) : (
          <Field>
            <FieldLabel htmlFor={fid('date')}>{UI_TEXT.PERIOD_FILTER_DATE_DIM(period)}</FieldLabel>
            {period === 'day' ? (
              <DateField
                id={fid('date')}
                label={UI_TEXT.PERIOD_FILTER_DATE_DIM(period)}
                value={date}
                max={today}
                onChange={(ymd) => {
                  setDate(ymd);
                  close();
                }}
              />
            ) : period === 'month' ? (
              <MonthField
                id={fid('date')}
                label={UI_TEXT.PERIOD_FILTER_DATE_DIM(period)}
                value={date.slice(0, 7)}
                max={today.slice(0, 7)}
                onChange={(ym) => {
                  setDate(`${ym}-01`);
                  close();
                }}
              />
            ) : (
              <SelectField
                id={fid('date')}
                label={UI_TEXT.PERIOD_FILTER_DATE_DIM(period)}
                options={YEAR_OPTIONS}
                value={date.slice(0, 4)}
                onChange={(year) => {
                  setDate(`${year}-01-01`);
                  close();
                }}
              />
            )}
          </Field>
        ),
    },
  ];
}

/**
 * 조회조건 기간 컨트롤(발전량·수익 공용) — **무상태**(상태=Applied, live). admin 조회 룩: **날짜기준 select** +
 * 값 필드 — 일=조회일자·월=조회월·연=조회연도(단일), 기간=시작일+종료일(역전·최대 span=입력 min/max + 훅
 * clampPeriodRange — 미래 차단만, span 상한 없음). `Field` 세트만 렌더(래핑·인접 필드는 호출처 controls 가 소유).
 *
 * 위 `periodFilterFields` 를 그대로 펼친 것 — 기간 차원 **외에 다른 차원을 섞어 쓰는 화면**(예: 발전량 조회의
 * 인버터·보기 방식)이 종전 배치를 유지하기 위한 얇은 어댑터다. 칩↔컨트롤 결합(칩 팝오버)이 필요하면
 * `periodFilterFields` + `FilterFieldStack` 을 쓴다.
 */
export function PeriodFilterControls(props: PeriodFilterControlsProps) {
  return (
    <>
      {periodFilterFields(props).map((field) => (
        <Fragment key={field.key}>{field.render(INLINE_FIELD_SURFACE)}</Fragment>
      ))}
    </>
  );
}
